import React, {useMemo, useState} from 'react';
import {Tooltip} from 'antd';
import {SettingOutlined, CommentOutlined, FileTextOutlined, MenuUnfoldOutlined} from '@ant-design/icons';
import AnalyseMetadataPanel from "../create/AnalyseMetadataPanel";
import AICommentSidebar from "../comments/AICommentSidebar";
import AutomatedResultsNotice from "../comments/AutomatedResultsNotice";
import './ReviewPage.css'
import { useTranslation } from 'react-i18next';

const IncompleteAnalysisWarning = ({ onClick }) => {
    const { t } = useTranslation('review');

    return (
        <Tooltip
            title={
                <div>
                    <div style={{ marginBottom: 4 }}>{t('incomplete_analysis.description')}</div>
                    <div style={{ fontWeight: 600 }}>{t('incomplete_analysis.cta')}</div>
                </div>
            }
            placement="bottom"
        >
            <div
                onClick={onClick}
                style={{
                    marginBottom: 10,
                    padding: '8px 12px',
                    background: '#fffbe6',
                    border: '1px solid #ffe58f',
                    borderRadius: 8,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#fff7cc';
                    e.currentTarget.style.borderColor = '#ffd666';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#fffbe6';
                    e.currentTarget.style.borderColor = '#ffe58f';
                }}
            >
                <span style={{ fontSize: 14, lineHeight: 1, flexShrink: 0 }}>⚠️</span>
                <span style={{ fontSize: 12, color: '#ad6800', fontWeight: 500, flex: 1 }}>
                    {t('incomplete_analysis.title')}
                </span>
                <span style={{ fontSize: 12, color: '#fa8c16', fontWeight: 600 }}>→</span>
            </div>
        </Tooltip>
    );
};

const RightPanel = ({
                        isCreate,
                        showSettings,
                        normalizedComments,
                        isIncomplete,
                        metadataInitialValues,
                        isGlobalLoading,
                        hoveredRef,
                        selectedRef,
                        selectedCommentId,
                        commentFocusRequest,
                        showAllComments,
                        onRightPanelModeChange,
                        onSaveMetadata,
                        onPovChange,
                        onMetadataDraftChange,
                        onGuessMetadata,
                        isGuessingMetadata,
                        onCommentHover,
                        onCommentClick,
                        onReferenceEvidenceClick,
                        onToggleShowAll,
                        onVisibleCommentsChange,
                        onStrategicMemoOpen,
                        focusStrategicMemoOnShow = false,
                        onCollapse,
                        analysisId,
                        documentVersionId,
                        partyDetection,
                        partyDetectionStatus,
                        povTransitioning = false,
                        povFocusRequest = 0,
                        hasCompleteAnalysisPov = false,
                        povResultsError = null,
                        onRetryPovResults,
                        onPovRequired,
                    }) => {
    const { t } = useTranslation("taxonomie");
    const [showAutomatedResultsIndicator, setShowAutomatedResultsIndicator] = useState(false);
    
    const hasComments = normalizedComments.length > 0;
    
    // Style dynamique : réduit la colonne quand vide
    // Grand écran (>= 1400px) : pas de réduction, taille normale (mode grid)
    // Petit écran (< 1400px) : colonne réduite mais confortable (mode flex)
    // ⚠️ Doit rester aligné avec le breakpoint @media de ReviewPage.css (1399px)
    //    et avec useUiState.js
    const isSmallScreen = typeof window !== 'undefined' && window.innerWidth < 1400;
    
    const dynamicStyle = useMemo(() => {
        if (hasComments) {
            // Avec commentaires : occuper l'espace mais 30% moins large qu'avant
            return {
                flex: '1 1 0%',
                minWidth: '0',
                maxWidth: 'none',
                width: 'auto',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
            };
        }
        // Sans commentaires
        if (!isSmallScreen) {
            // Grand écran : taille normale, pas de réduction
            return { transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)' };
        }
        // Petit écran : colonne réduite mais assez large pour les placeholders
        return {
            flex: '0 0 420px',
            minWidth: '380px',
            maxWidth: '460px',
            width: '420px',
            transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
        };
    }, [hasComments, isSmallScreen]);

    return (
        <div className="review-col comments-col" style={dynamicStyle}>
            {/* Header avec toggle */}
            <div className="column-header column-header--comments">
                <div className="column-header__left">
                    {showSettings ? (
                        <>
                            <SettingOutlined className="column-header__icon"/>
                            <span className="column-header__title">
                                {t('common.contract_details')}
                            </span>
                        </>
                    ) : (
                        <>
                            <CommentOutlined className="column-header__icon"/>
                            <span className="column-header__title">
                                {t('common.comments')}
                                {normalizedComments.length > 0 && (
                                    <span className="column-header__count">
                                        ({normalizedComments.length})
                                    </span>
                                )}
                            </span>
                            {showAutomatedResultsIndicator && (
                                <AutomatedResultsNotice variant="header" />
                            )}
                        </>
                    )}
                </div>
                <div className="column-header__right">
                    {!isCreate && (
                        <Tooltip
                            title={t('common.hide_comments', {defaultValue: 'Masquer les commentaires'})}
                            placement="bottom"
                            mouseEnterDelay={0}
                            mouseLeaveDelay={0}
                        >
                            <button
                                type="button"
                                className="comments-panel-collapse-btn"
                                onClick={onCollapse}
                                aria-label={t('common.hide_comments', {defaultValue: 'Masquer les commentaires'})}
                                title={t('common.hide_comments', {defaultValue: 'Masquer les commentaires'})}
                            >
                                <MenuUnfoldOutlined/>
                            </button>
                        </Tooltip>
                    )}
                    {!isCreate && (
                        <button
                            onClick={() => onRightPanelModeChange(showSettings ? "comments" : "settings")}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '5px 12px',
                                border: '1px solid #e8e8e8',
                                borderRadius: 7,
                                cursor: 'pointer',
                                fontSize: 12,
                                fontWeight: 500,
                                color: '#595959',
                                background: '#fafafa',
                                transition: 'all 0.2s ease',
                                whiteSpace: 'nowrap',
                                lineHeight: 1,
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.background = '#fafafa';
                                e.currentTarget.style.borderColor = '#d9d9d9';
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.color = '#595959';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.background = '#fafafa';
                                e.currentTarget.style.borderColor = '#e8e8e8';
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.color = '#595959';
                            }}
                        >
                            {showSettings
                                ? <><CommentOutlined style={{ fontSize: 12 }} /> {t('common.comments')}</>
                                : <><FileTextOutlined style={{ fontSize: 12 }} /> {t('common.contract_details')}</>
                            }
                        </button>
                    )}
                </div>
            </div>

            {/* Contenu du panneau — animated */}
            <div
                key={showSettings ? 'settings' : 'comments'}
                className={showSettings ? undefined : 'review-comments-content'}
                style={{
                    animation: 'panelContentIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) both',
                    flex: 1,
                    overflow: showSettings ? 'hidden auto' : 'hidden',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {showSettings ? (
                <AnalyseMetadataPanel
                    mode={isCreate ? "create" : "update"}
                    initialValues={metadataInitialValues}
                    onChangeDraft={onMetadataDraftChange}
                    onSave={isCreate ? undefined : onSaveMetadata}
                    onPovChange={isCreate ? undefined : onPovChange}
                    onGuess={onGuessMetadata}
                    guessing={isGuessingMetadata}
                    loading={isGlobalLoading}
                    disabled={false}
                    partyDetection={partyDetection}
                    partyDetectionStatus={partyDetectionStatus}
                    povTransitioning={povTransitioning}
                    povFocusRequest={povFocusRequest}
                />
            ) : (
                <>
                    {isIncomplete && (
                        <IncompleteAnalysisWarning
                            onClick={() => onRightPanelModeChange("settings")}
                        />
                    )}

                    <AICommentSidebar
                        comments={normalizedComments}
                        hoveredRef={hoveredRef}
                        selectedRef={selectedRef}
                        selectedCommentId={selectedCommentId}
                        commentFocusRequest={commentFocusRequest}
                        onCommentHover={onCommentHover}
                        onCommentClick={onCommentClick}
                        onReferenceEvidenceClick={onReferenceEvidenceClick}
                        showAll={showAllComments}
                        onToggleShowAll={onToggleShowAll}
                        onVisibleCommentsChange={onVisibleCommentsChange}
                        analysisId={analysisId}
                        documentVersionId={documentVersionId}
                        onStrategicMemoOpen={onStrategicMemoOpen}
                        onAutomatedResultsBannerVisibilityChange={setShowAutomatedResultsIndicator}
                        focusStrategicMemoOnShow={focusStrategicMemoOnShow}
                        hasCompleteAnalysisPov={hasCompleteAnalysisPov}
                        povTransitioning={povTransitioning}
                        povResultsError={povResultsError}
                        onRetryPovResults={onRetryPovResults}
                        onPovRequired={onPovRequired}
                    />
                </>
            )}
            </div>
        </div>
    );
};

export default RightPanel;
